import React, { useMemo } from "react";

/** Простой SVG-штрихкод для вертикальной ленты (увеличенная высота) */
function BarcodeSVG({ code = "0000000000000", width = 140, height = 40 }) {
    const bars = useMemo(() => {
        const map = {
            "0":[1,1,0,1,0], "1":[1,0,1,1,0], "2":[1,0,1,0,1], "3":[1,1,0,0,1],
            "4":[1,0,0,1,1], "5":[1,0,1,1,1], "6":[0,1,1,1,0], "7":[0,1,0,1,1],
            "8":[0,1,1,0,1], "9":[1,1,1,0,0]
        };
        const seq = [];
        for (const ch of String(code)) { seq.push(...(map[ch] || [1,0,1,0,1]), 0); }
        return seq;
    }, [code]);

    const barW = Math.max(1, Math.floor(width / bars.length));
    const xTotal = barW * bars.length;
    const offsetX = Math.floor((width - xTotal) / 2);

    const barHeight = Math.max(18, height - 12); // оставляем место под цифры

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-label={`barcode-${code}`}>
            <rect x="0" y="0" width={width} height={height} fill="#fff" />
            {bars.map((b, i) =>
                b ? (
                    <rect
                        key={i}
                        x={offsetX + i * barW}
                        y="0"
                        width={barW}
                        height={barHeight}
                        fill="#000"
                    />
                ) : null
            )}
            <text
                x={width / 2}
                y={height - 2}
                textAnchor="middle"
                fontFamily="monospace"
                fontSize="9"
                fill="#000"
            >
                {code}
            </text>
        </svg>
    );
}

/**
 * LabelsSheet — вертикальная лента этикеток для компьютера/принтера
 * props:
 *  - items: [{title,name,article,brand,category,subcategory,purchaseDate,supplier,barcode,quantity}]
 *  - duplicateByQuantity: дублировать карточку по quantity (true по умолчанию)
 *  - containerMode: 'standalone' | 'embedded' - режим отображения контейнера
 */
export default function LabelsSheet({ items = [], duplicateByQuantity = true, containerMode = 'standalone' }) {
    const expanded = useMemo(() => {
        if (!duplicateByQuantity) return items;
        const out = [];
        (items || []).forEach(it => {
            const q = Math.max(1, Number(it.quantity) || 1);
            for (let i = 0; i < q; i++) out.push({ ...it });
        });
        return out;
    }, [items, duplicateByQuantity]);

    return (
        <div className="labels-vertical-tape-container">
            <div className="labels-vertical-tape">
                {expanded.map((it, idx) => (
                    <div key={idx} className="label-vertical-item">
                        <div className="label-content">
                            <div className="label-header">
                                <div className="label-title">{it.title || it.name || "Товар"}</div>
                                <div className="label-article">#{it.article || "—"}</div>
                            </div>

                            <div className="label-barcode">
                                <BarcodeSVG code={it.barcode || "0000000000000"} width={140} height={40} />
                            </div>

                            <div className="label-info">
                                <div className="info-row">
                                    <span className="info-label">Бренд:</span>
                                    <span className="info-value">{it.brand || "—"}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Категория:</span>
                                    <span className="info-value">{it.category || "—"}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Дата:</span>
                                    <span className="info-value">{it.purchaseDate || it.date || "—"}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Поставщик:</span>
                                    <span className="info-value">{it.supplier || "—"}</span>
                                </div>
                            </div>
                        </div>

                        {/* Аккуратный разделитель — ТОЛЬКО пунктир, без насечек */}
                        {idx < expanded.length - 1 && <div className="label-separator" />}
                    </div>
                ))}
            </div>            <style>{`
        .labels-vertical-tape-container {
          width: 100%;
          ${containerMode === 'embedded' ? 'height: auto; overflow: visible;' : 'height: 100vh; overflow-y: auto; overflow-x: hidden;'}
          background: #f0f0f0;
          display: flex;
          justify-content: center;
          padding: 20px 0;
        }
        .labels-vertical-tape {
          width: 300px;
          background: #fff;
          border: 2px solid #333;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          position: relative;
          min-height: max-content;
        }

        .label-vertical-item { position: relative; }
        .label-content {
          padding: 10px 12px;             /* чуть больше внутренние отступы */
          background: #fff;
          font-family: Arial, sans-serif;
          break-inside: avoid;
          page-break-inside: avoid;
        }

        .label-header { margin-bottom: 8px; text-align: center; }
        .label-title {
          font-size: 12px; font-weight: 700; color: #333; line-height: 1.2;
          margin-bottom: 2px; word-wrap: break-word; max-height: 36px; overflow: hidden;
          display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;
        }
        .label-article { font-size: 10px; color: #666; font-weight: 600; }

        .label-barcode {
          display: flex;
          justify-content: center;
          margin: 12px 0 16px 0;         /* ↑ больше “воздуха” вокруг кода */
          background: #fff;
          border-radius: 3px;
        }

        .label-info { font-size: 9px; line-height: 1.35; }
        .info-row { display: flex; justify-content: space-between; margin-bottom: 2px; align-items: flex-start; }
        .info-label { color: #666; white-space: nowrap; margin-right: 4px; min-width: 60px; }
        .info-value { color: #333; font-weight: 500; text-align: right; flex: 1; word-wrap: break-word; overflow-wrap: break-word; hyphens: auto; }

        /* --- Разделитель: чистый пунктир без боковых линий --- */
        .label-separator {
          margin: 8px 6px 12px 6px;
          height: 0;
          border-top: 0.4pt dashed #000;
        }

        /* Адаптив */
        @media (max-width: 768px) {
          .labels-vertical-tape { width: 180px; }
          .label-content { padding: 8px 10px; }
          .label-title { font-size: 11px; }
          .label-article { font-size: 9px; }
          .info-row { font-size: 8px; }
          .info-label { min-width: 50px; }
        }
        @media (max-width: 480px) {
          .labels-vertical-tape-container { padding: 10px 5px; }
          .labels-vertical-tape { width: 160px; }
          .label-content { padding: 6px 8px; }
          .label-title { font-size: 10px; }
          .info-row { font-size: 7px; }
        }        /* Стили для встроенного режима */
        ${containerMode === 'embedded' ? `
          .labels-vertical-tape-container {
            background: transparent !important;
            padding: 10px 0 !important;
          }
        ` : ''}

        /* Печать — чистый вид для рулонных принтеров */
        @media print {
          .labels-vertical-tape-container {
            background: transparent;
            height: auto;
            padding: 0;
          }
          .labels-vertical-tape {
            border: 1px solid #000;
            box-shadow: none;
            width: 180px;                 /* подстрой под ширину рулона вашего принтера */
          }
          /* никакой боковой декорации */
          .labels-vertical-tape::before,
          .labels-vertical-tape::after { display: none; }

          /* разделитель остаётся пунктиром */
          .label-separator {
            margin: 4mm 2mm;
            border-top: 0.4pt dashed #000;
          }
        }
      `}</style>
        </div>
    );
}
